import type {
  Alert,
  AppConfig,
  ForecastBundle,
  GeocodeResult,
  Glance,
  NowReading,
  NowStep,
  Place,
  PlaceHit,
  PushConfig,
} from './types'

export class ApiError extends Error {
  readonly status: number
  readonly detail: string

  constructor(status: number, detail: string) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

/** FastAPI errors are `{"detail": "..."}`; fall back to the status text. */
async function toApiError(resp: Response): Promise<ApiError> {
  let detail = resp.statusText || `HTTP ${resp.status}`
  try {
    const body = await resp.json()
    if (body && typeof body.detail === 'string') detail = body.detail
  } catch {
    // Not JSON (a proxy error page, say). The status text will do.
  }
  return new ApiError(resp.status, detail)
}

/**
 * When the service worker replays a cached response it replays the original
 * headers too, so `Date` is when the forecast actually came from NWS. Computing
 * this client side would make the offline "updated N minutes ago" line lie.
 */
function responseDate(resp: Response): Date {
  const raw = resp.headers.get('date')
  if (raw) {
    const d = new Date(raw)
    if (!Number.isNaN(d.getTime())) return d
  }
  return new Date()
}

/** Config is a nicety, not a dependency: a failure just means no CARTO key. */
export async function fetchConfig(): Promise<AppConfig> {
  try {
    const resp = await fetch('/api/config')
    if (!resp.ok) throw await toApiError(resp)
    const body = (await resp.json()) as Partial<AppConfig>
    return {
      carto_api_key: typeof body.carto_api_key === 'string' ? body.carto_api_key : '',
      push: pushConfig(body.push),
    }
  } catch (e) {
    console.warn('config fetch failed, continuing without one', e)
    return { carto_api_key: '' }
  }
}

const KEY_RE = /^[A-Za-z0-9_-]{80,100}$/
const KEY_ID_RE = /^[0-9a-f]{16}$/

/** A malformed key is treated as push being off: subscribing with it would fail anyway. */
function pushConfig(raw: unknown): PushConfig | null {
  if (!raw || typeof raw !== 'object') return null
  const { public_key, key_id, apns } = raw as Partial<PushConfig>
  if (typeof public_key !== 'string' || !KEY_RE.test(public_key)) return null
  if (typeof key_id !== 'string' || !KEY_ID_RE.test(key_id)) return null
  // Whether the iOS app can subscribe here. Only `true` means yes.
  return { public_key, key_id, apns: apns === true }
}

export async function geocodeZip(zip: string): Promise<GeocodeResult> {
  const resp = await fetch(`/api/geocode/zip/${encodeURIComponent(zip)}`)
  if (!resp.ok) throw await toApiError(resp)
  return (await resp.json()) as GeocodeResult
}

/**
 * Places whose name starts with `query`, from the server's own Census table,
 * so it is asked as the name is typed. An empty list is an answer ("no place
 * is called that"), not an error.
 */
export async function searchPlaces(query: string, signal?: AbortSignal): Promise<PlaceHit[]> {
  const resp = await fetch(`/api/geocode/search?q=${encodeURIComponent(query)}`, { signal })
  if (!resp.ok) throw await toApiError(resp)
  const body = (await resp.json()) as { results?: PlaceHit[] }
  return body.results ?? []
}

// One request in flight at a time. Without this a slow response for an old
// point can land after a fast one for a new point and overwrite it.
let inFlight: AbortController | null = null

export interface ForecastResult {
  bundle: ForecastBundle
  fetchedAt: Date
}

export async function fetchForecast(lat: number, lon: number): Promise<ForecastResult> {
  inFlight?.abort()
  const controller = new AbortController()
  inFlight = controller

  try {
    const resp = await fetch(`/api/forecast?lat=${lat}&lon=${lon}`, { signal: controller.signal })
    if (!resp.ok) throw await toApiError(resp)
    const bundle = (await resp.json()) as ForecastBundle
    return { bundle, fetchedAt: responseDate(resp) }
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/**
 * The nearest place to a point, for the map menu's header.
 *
 * One upstream request (~1 KB gzipped) against the forecast bundle's six, and
 * it shares the bundle's `/points` cache entry, so it doubles as a prefetch
 * for "Forecast here". A point outside NWS coverage is a 404 and the menu
 * falls back to coordinates.
 */
export async function fetchPlace(lat: number, lon: number): Promise<Place> {
  const resp = await fetch(`/api/place?lat=${lat}&lon=${lon}`)
  if (!resp.ok) throw await toApiError(resp)
  return (await resp.json()) as Place
}

/**
 * One place's weather at a glance, for its row in Places. Checked rather than
 * cast, like `fetchNow`: a body of another shape (a proxy's page, an older
 * server's SPA fallback) must be a failed row, not a row of `undefined`s.
 */
export async function fetchGlance(
  lat: number,
  lon: number,
  signal?: AbortSignal,
): Promise<{ glance: Glance; fetchedAt: Date }> {
  const resp = await fetch(`/api/glance?lat=${lat}&lon=${lon}`, { signal })
  if (!resp.ok) throw await toApiError(resp)
  const body = (await resp.json()) as Partial<Glance> | null
  if (
    !body ||
    typeof body.location !== 'object' ||
    !body.today ||
    typeof body.today !== 'object' ||
    !Array.isArray(body.alerts) ||
    (body.current !== null && typeof body.current !== 'object')
  ) {
    throw new ApiError(502, 'not a glance')
  }
  // The Date header, as for the forecast: a row the service worker replays
  // offline reads as old and is asked for again.
  return { glance: body as Glance, fetchedAt: responseDate(resp) }
}

/**
 * One alert by CAP id, for the map's tap popup. The id goes in the path
 * unencoded-but-escaped; the backend validates it against the CAP shape before
 * it builds an upstream URL from it.
 */
export async function fetchAlert(capId: string): Promise<Alert> {
  const resp = await fetch(`/api/alerts/${encodeURIComponent(capId)}`)
  if (!resp.ok) throw await toApiError(resp)
  return (await resp.json()) as Alert
}

/**
 * The radar at a point (`/api/now`). The body is checked rather than cast:
 * this drives the hero's words, and a body of some other shape — a proxy's
 * page, an older server's catch-all — must mean "the radar says nothing", not
 * a label of `undefined`.
 */
export async function fetchNow(lat: number, lon: number, signal?: AbortSignal): Promise<NowReading> {
  const resp = await fetch(`/api/now?lat=${lat}&lon=${lon}`, { signal })
  if (!resp.ok) throw await toApiError(resp)
  const body = (await resp.json()) as Partial<NowReading>
  if (
    body?.source !== 'MRMS' ||
    typeof body.time !== 'string' ||
    typeof body.coverage !== 'boolean' ||
    typeof body.precipitating !== 'boolean' ||
    (body.label !== null && typeof body.label !== 'string')
  ) {
    throw new ApiError(502, 'not a radar reading')
  }
  // An older server sends no next hour at all, and a malformed one is no
  // next hour rather than no reading: the hero's words do not depend on it.
  const minutes = Array.isArray(body.minutes) && body.minutes.every(isStep) ? body.minutes : null
  return { ...(body as NowReading), minutes, motion: minutes ? (body.motion ?? null) : null }
}

function isStep(step: unknown): step is NowStep {
  const s = step as Partial<NowStep> | null
  return (
    !!s &&
    typeof s.lead === 'number' &&
    typeof s.coverage === 'boolean' &&
    typeof s.precipitating === 'boolean' &&
    typeof s.level === 'number' &&
    (s.label === null || typeof s.label === 'string')
  )
}

/** Test seam: drop the module-level controller between cases. */
export function _resetInFlight(): void {
  inFlight = null
}
